<template>
  <CustomModal ref="modal" :title="isEdit ? 'Modifier la réception' : 'Réception de marchandises'" class="reception-modal">
    <div class="container">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <p v-if="purchase" class="recap">
          <span class="recap-supplier">{{ supplierName }}</span>
          <span class="recap-purchase">{{ purchaseLabel }}</span>
        </p>
        <div class="fields-row">
          <CustomTextField :content=inputs.date />
          <CustomTextField :content=inputs.reference />
        </div>

        <div class="section">
          <div class="section-header">
            <p class="section-label">Quantités reçues</p>
            <button type="button" class="link-button" @click="receiveAll">Tout reçu</button>
          </div>
          <div v-for="row in rows" :key="row.lineId" class="reception-row">
            <div class="designation">
              <strong :title="row.designation">{{ row.designation }}</strong>
              <span class="hint">{{ formatQuantity(row.alreadyReceived, row.unit) }} déjà reçu sur {{ formatQuantity(row.ordered, row.unit) }}</span>
            </div>
            <div class="quantity">
              <CustomTextField :content=row.quantity />
              <span v-if="row.unit" class="suffix">{{ row.unit }}</span>
            </div>
          </div>
        </div>

        <CustomTextField :content=inputs.notes />
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
          <CustomButton v-if="isEdit" class="button remove" :is-secondary="true" @click="remove" :params=buttons.remove />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
  <ConfirmModal ref="deleteConfirmModal" title="Suppression" text="Supprimer cette réception ? Les quantités reçues de l'achat seront diminuées d'autant." yes="Supprimer" no="Retour"/>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import {convertISOToDate} from "@/utils";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {ReceptionController} from "@/purchase/controllers/ReceptionController";
import {formatQuantity, idOf, roundQuantity} from "@/purchase/utils";

// Modale d'une réception de marchandises (livraison, en totalité ou en partie, d'un achat de biens).
// - openModal({purchaseId, supplierName}) : nouvelle réception, les quantités proposées sont ce qu'il reste à recevoir
// - openModal({reception}) : modification (ou suppression) d'une réception existante (ligne de ReceptionController.getByPurchase)
// callback est appelé après chaque enregistrement ou suppression.
export default {
  name: 'ReceptionModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomTextField, CustomButton, ConfirmModal},
  data() {
    return {
      receptionId: undefined,
      purchase: undefined,
      supplierName: '',
      rows: [],
      inputs: {
        date: {label: 'Date de réception', value: convertISOToDate(new Date()), type: 'date', required: true},
        reference: {label: 'N° du bon de livraison', value: '', type: 'text'},
        notes: {label: 'Notes', value: '', type: 'text'},
      },
      buttons: {
        submit: {text: "Enregistrer"},
        cancel: {text: "Annuler"},
        remove: {text: "Supprimer"},
      }
    };
  },
  computed: {
    isEdit() {
      return this.receptionId !== undefined;
    },
    purchaseLabel() {
      if (!this.purchase) return '';
      return this.purchase.reference ? `${this.purchase.label} (${this.purchase.reference})` : this.purchase.label;
    },
  },
  methods: {
    formatQuantity,
    async openModal({purchaseId, supplierName, reception} = {}) {
      this.receptionId = reception ? idOf(reception) : undefined;
      this.purchase = undefined;
      this.rows = [];
      this.supplierName = supplierName ?? reception?.supplierName ?? '';
      this.inputs.date.value = reception ? convertISOToDate(reception.date) : convertISOToDate(new Date());
      this.inputs.reference.value = reception?.reference ?? '';
      this.inputs.notes.value = reception?.notes ?? '';
      this.$refs.modal.openModal();

      const purchase = await PurchaseController.get(purchaseId ?? reception.purchaseId);
      if (!purchase || purchase.errors) {
        this.closeModal(true);
        return;
      }
      this.purchase = purchase;

      // Les quantités déjà reçues sont celles des autres réceptions : celle que l'on modifie est remplacée par la saisie
      const own = new Map((reception?.lines ?? []).map(line => [line.lineId, line.quantity]));
      this.rows = (purchase.lines ?? []).map(line => {
        const alreadyReceived = roundQuantity((line.receivedQuantity ?? 0) - (own.get(line.id) ?? 0));
        const remaining = roundQuantity(line.quantity - alreadyReceived);
        const quantity = reception ? (own.get(line.id) ?? '') : (remaining > 0 ? remaining : '');
        return {
          lineId: line.id, designation: line.designation, unit: line.unit, ordered: line.quantity, alreadyReceived, remaining,
          quantity: {value: quantity, type: 'number', step: 'any', placeholder: '0'},
        };
      });
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    receiveAll() {
      this.rows.forEach(row => { row.quantity.value = row.remaining > 0 ? row.remaining : ''; });
    },
    fail(input, message) {
      if (input) input.error = true;
      (new NotificationService).addNotification("danger", message);
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);
      if (!this.inputs.date.value) return this.fail(this.inputs.date, "La date n'est pas renseignée");

      const lines = [];
      for (const row of this.rows) {
        if (String(row.quantity.value ?? '').trim() === '') continue;
        const quantity = Number(String(row.quantity.value).replace(',', '.'));
        if (!(quantity > 0)) return this.fail(row.quantity, "Une quantité n'est pas valide");
        if (quantity > row.remaining + 0.0005) return this.fail(row.quantity, `Une quantité dépasse ce qu'il reste à recevoir (${formatQuantity(row.remaining, row.unit)})`);
        lines.push({lineId: row.lineId, quantity});
      }
      if (lines.length === 0) return this.fail(undefined, "Aucune quantité reçue n'est renseignée");

      const body = {
        date: this.inputs.date.value,
        reference: String(this.inputs.reference.value ?? '').trim(),
        notes: String(this.inputs.notes.value ?? '').trim(),
        lines,
      };
      const res = await this.$refs.form.submitForm(() => this.isEdit
          ? ReceptionController.patch(this.receptionId, body)
          : ReceptionController.post({...body, purchaseId: idOf(this.purchase)}));
      if (!res.errors) {
        (new NotificationService).addNotification("success", this.isEdit ? "Réception modifiée" : "Réception enregistrée");
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    },
    async remove() {
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => ReceptionController.delete(this.receptionId));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Réception supprimée");
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/purchase/style/section.styl';

.container
  display flex
  flex-direction column
  gap 10px

  form
    display flex
    flex-direction column
    gap 10px

    .recap
      display flex
      flex-direction column
      margin 0

      .recap-supplier
        font-size 1.2em
        font-weight 600
        color THEME_COLOR_PRIMARY

      .recap-purchase
        color #6b7280

    .fields-row
      display grid
      grid-template-columns 1fr 1fr
      gap 12px

    .section
      margin-top 0

    .reception-row
      display grid
      grid-template-columns 1fr 150px
      align-items center
      gap 12px
      padding 4px 0

      .designation
        display flex
        flex-direction column
        min-width 0

        strong
          overflow hidden
          text-overflow ellipsis
          white-space nowrap

      .quantity
        position relative

        .suffix
          position absolute
          right 14px
          top 50%
          transform translateY(-50%)
          font-size 0.8em
          color #9ca3af
          pointer-events none

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Enregistrer]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 4px

      .button
        min-width 110px

      .remove
        margin-right auto

@media (max-width 480px)
  .container form
    .fields-row
      grid-template-columns 1fr

    .reception-row
      grid-template-columns 1fr 120px
</style>

<style lang="stylus">
.reception-modal
  > .modal
    width 560px !important
    max-width 100%
    height auto !important
    max-height 90vh
    overflow-y auto
</style>
